import { AnalyticsWidgetRegistry } from "../screens/analytics/widgets/types";
import { ChartType } from "../types/analytics";
import DailyVideoEngagementOldWidget from "../screens/analytics/widgets/DailyVideoEngagementOldWidget";
import PlayWidget from "../screens/analytics/widgets/PlayWidget";
import SlotPerformanceWidget from "../screens/analytics/widgets/SlotPerformanceWidget";
import RecommendationEfficiencyWidget from "../screens/analytics/widgets/RecommendationEfficiencyWidget";
import ExperimentComparisonWidget from "../screens/analytics/widgets/ExperimentComparisonWidget";
import ChartNameUsageWidget from "../screens/analytics/widgets/ChartNameUsageWidget";


/**
 * Registry of all available analytics widgets
 * 
 * Each widget is configured with:
 * - key: Unique identifier used in API calls and customer configs
 * - displayName: Human-readable name shown in UI
 * - component: React component to render
 * - supportedFilters: Which filters the widget uses
 * - customers: Array of customer names, or ["*"] for all
 * - chartType: Type of chart rendered
 */
/* eslint-disable camelcase */
export const ANALYTICS_WIDGETS: AnalyticsWidgetRegistry = {
    daily_video_engagement_old: {
        key: "daily_video_engagement_old",
        displayName: "Daily Video Engagement (Old)",
        component: DailyVideoEngagementOldWidget,
        supportedFilters: ["startDate", "endDate", "devicePlatform", "brandId", "attributionId" ],
        customers: ["*"],
        chartType: ChartType.LINE,
        description: "Video engagement metrics including starts, completions, and progress milestones"
    },
    play_events: {
        key: "play_events",
        displayName: "Play Events Over Time",
        component: PlayWidget,
        supportedFilters: ["startDate", "endDate", "devicePlatform", "brandId", "attributionId" ],
        customers: ["*"],
        chartType: ChartType.LINE,
        description: "Play events over time"
    },
    /* eslint-disable camelcase */
    slot_performance: {
        key: "slot_performance",
        displayName: "Slot Performance",
        component: SlotPerformanceWidget,
        supportedFilters: ["startDate", "endDate", "attributionId", "brandId", "devicePlatform", "focusField"],
        customers: ["*"],
        chartType: ChartType.BAR,
        description: "Play counts and conversion rates by recommendation slot"
    },
    recommendation_efficiency: {
        key: "recommendation_efficiency",
        displayName: "Recommendation Efficiency",
        component: RecommendationEfficiencyWidget,
        supportedFilters: ["startDate", "endDate"],
        customers: ["*"],
        chartType: ChartType.AREA,
        description: "Execution time vs items returned by experiment"
    },
    experiment_comparison: {
        key: "experiment_comparison",
        displayName: "Experiment Comparison",
        component: ExperimentComparisonWidget,
        supportedFilters: ["startDate", "endDate", "attributionId", "brandId", "devicePlatform"],
        customers: ["*"],
        chartType: ChartType.BAR,
        description: "A/B test comparison with conversion rates"
    },
    chartname_usage: {
        key: "chartname_usage",
        displayName: "Chart Name Usage",
        component: ChartNameUsageWidget,
        supportedFilters: ["startDate", "endDate", "attributionId", "brandId", "devicePlatform"],
        customers: ["*"],
        chartType: ChartType.BAR,
        description: "Usage distribution by recommendation chart name"
    }
    /* eslint-enable camelcase */
};

/**
 * Get default set of widgets to display when not configured for a customer
 */
export const DEFAULT_ANALYTICS_WIDGETS: string[] = [
    "play_events",
    "daily_video_engagement_old",
    "slot_performance",
    "recommendation_efficiency",
    "experiment_comparison",
    "chartname_usage"
];

/**
 * Get widgets enabled for a specific customer
 * Falls back to default widgets if customer doesn't have config
 */
export function getEnabledWidgets(customerConfig: CustomerConfig): string[] {
    if (customerConfig.analyticsWidgets?.enabled) {
        return customerConfig.analyticsWidgets.enabled;
    }
    
    // Fallback to defaultWidgets or global default
    if (customerConfig.analyticsWidgets?.defaultWidgets) {
        return customerConfig.analyticsWidgets.defaultWidgets;
    }
    
    return DEFAULT_ANALYTICS_WIDGETS;
}

/**
 * Filter widgets by customer access
 */
export function getWidgetsForCustomer(
    enabledKeys: string[],
    customerName: string
): typeof ANALYTICS_WIDGETS {
    const filtered: typeof ANALYTICS_WIDGETS = {};
    
    for (const key of enabledKeys) {
        const widget = ANALYTICS_WIDGETS[key];
        if (!widget) continue;
        
        // Check if customer has access (* means all customers)
        if (widget.customers.includes("*") || widget.customers.includes(customerName)) {
            filtered[key] = widget;
        }
    }
    
    return filtered;
}
